import { type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';

/**
 * ModernStatusBadge — quiet, muted status pills. Readable but never loud:
 * low-saturation tinted fills with a soft dot, no heavy solid colors.
 */
const modernBadgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium',
  {
    variants: {
      tone: {
        neutral: 'bg-muted text-muted-foreground',
        accent: 'bg-primary/[0.08] text-primary',
        success: 'bg-success/10 text-success',
        warning: 'bg-warning/12 text-warning',
        info: 'bg-info/10 text-info',
        danger: 'bg-destructive/10 text-destructive',
        outline: 'border border-border text-muted-foreground',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

const dotTone: Record<string, string> = {
  neutral: 'bg-muted-foreground/60',
  accent: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  info: 'bg-info',
  danger: 'bg-destructive',
  outline: 'bg-muted-foreground/60',
};

export interface ModernStatusBadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof modernBadgeVariants> {
  /** Show the leading status dot. Default true. */
  dot?: boolean;
}

export function ModernStatusBadge({
  className,
  tone = 'neutral',
  dot = true,
  children,
  ...props
}: ModernStatusBadgeProps) {
  return (
    <span className={cn(modernBadgeVariants({ tone }), className)} {...props}>
      {dot && (
        <span
          className={cn('h-1.5 w-1.5 flex-shrink-0 rounded-full', dotTone[tone ?? 'neutral'])}
          aria-hidden
        />
      )}
      {children}
    </span>
  );
}

export { modernBadgeVariants };
